/**
 * Inline ZIP-factor editor (Spec 016).
 *
 * Renders as an expanded form below the ZIP row. Saves via
 * `patchZipFactor` with `If-Match` optimistic concurrency. `zip` is
 * the PK and not editable.
 */

import { useState } from "react";

import { patchZipFactor } from "@starz/shared/api/sdk.gen";
import type { ZipFactor } from "@starz/shared/api/types.gen";

interface Props {
  factor: ZipFactor;
  onSaved: (factor: ZipFactor) => void;
  onCancel: () => void;
}

export function ZipRowEditor({ factor, onSaved, onCancel }: Props) {
  const [factorValue, setFactorValue] = useState(String(factor.factor));
  const [notes, setNotes] = useState(factor.notes ?? "");
  const [active, setActive] = useState(factor.active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSave() {
    setError(null);
    const f = parseFloat(factorValue);
    if (Number.isNaN(f) || f <= 0 || f > 5.0) {
      setError("Factor must be between 0 and 5.0.");
      return;
    }
    setSaving(true);
    const result = await patchZipFactor({
      zip: factor.zip,
      ifMatchVersion: factor.version,
      body: { factor: f, notes: notes.trim() || null, active },
    });
    setSaving(false);
    if ("error" in result) {
      setError(
        result.error.error.code === "CONFLICT"
          ? "This ZIP factor changed elsewhere — reload and retry."
          : result.error.error.message,
      );
      return;
    }
    onSaved(result.data);
  }

  const inputClass =
    "mt-0.5 w-full rounded-md border border-starz-gold/20 bg-white px-2 py-1.5 text-sm";

  return (
    <div className="space-y-3 rounded-lg border border-starz-gold/20 bg-starz-cream/40 p-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            ZIP (read-only)
          </label>
          <input
            type="text"
            value={factor.zip}
            disabled
            className={`${inputClass} bg-starz-cream text-warm-gray`}
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
            Factor (0–5.0)
          </label>
          <input
            type="number"
            step="0.001"
            min="0"
            max="5"
            value={factorValue}
            onChange={(e) => setFactorValue(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-warm-gray">
          Notes
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={inputClass}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-starz-charcoal">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4"
        />
        Active
      </label>
      {error ? (
        <p className="border-l-2 border-starz-brick pl-3 text-xs text-starz-brick">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md border border-starz-gold/30 px-3 py-1.5 text-xs font-medium text-starz-charcoal hover:bg-white"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void onSave()}
          disabled={saving}
          className="rounded-md bg-starz-navy px-4 py-1.5 text-xs font-semibold text-starz-off-white hover:opacity-90 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
